import { useCallback, useEffect, useRef, useState } from 'react';
import config from '../config';
import {
  fetchDeployedBuildTime,
  isNewerBuildDeployed,
  triggerServiceWorkerUpdate,
} from '../utils/appVersion';

function getLocalBuildTime(): string {
  return import.meta.env.VITE_BUILD_TIME ?? '';
}

function shouldPerformVersionCheck(): boolean {
  return (
    import.meta.env.PROD &&
    config.pwaEnabled &&
    getLocalBuildTime().length > 0
  );
}

export function useAppVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const checkingRef = useRef(false);

  const checkForUpdate = useCallback(async () => {
    if (!shouldPerformVersionCheck() || checkingRef.current) {
      return;
    }

    const localBuildTime = getLocalBuildTime();
    checkingRef.current = true;
    try {
      await triggerServiceWorkerUpdate();
      const remoteBuildTime = await fetchDeployedBuildTime();
      if (
        remoteBuildTime &&
        isNewerBuildDeployed(localBuildTime, remoteBuildTime)
      ) {
        setUpdateAvailable(true);
      }
    } finally {
      checkingRef.current = false;
    }
  }, []);

  const refresh = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    if (!shouldPerformVersionCheck()) {
      return;
    }

    checkForUpdate();

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        checkForUpdate();
      }
    }, config.pwaUpdateCheckInterval);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdate();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkForUpdate]);

  return { updateAvailable, refresh };
}
